import {
  TenantAccountingSettings,
  TenantRoutingSettings
} from '../../../providers/User/User.model';
import { PaginateResponse } from '../../Global.model';
import { twApi } from '../api';
import { SearchTenantField, SearchTenantFieldParams } from './tenant.model';

const tenantApi = twApi.injectEndpoints({
  endpoints: (builder) => ({
    getTenantAccountingSettings: builder.query<TenantAccountingSettings, { divisionId: string }>({
      query: ({ divisionId }) => ({
        url: '/tenant/accounting-settings',
        method: 'GET',
        params: {
          divisionId
        }
      }),
      transformResponse: (response: { data: TenantAccountingSettings }) => response.data,
      providesTags: () => [{ type: 'TenantAccountingSettings', id: 'TENANT_ACCOUNTING_SETTINGS' }]
    }),
    upsertTenantAccountingSettings: builder.mutation<
      TenantAccountingSettings,
      TenantAccountingSettings
    >({
      query: (body) => ({
        url: '/tenant/accounting-settings',
        method: 'POST',
        body
      }),
      transformResponse: (response: { data: TenantAccountingSettings }) => response.data,
      invalidatesTags: [
        { type: 'TenantAccountingSettings', id: 'TENANT_ACCOUNTING_SETTINGS' },
        'InvoiceService'
      ]
    }),
    getTenantRoutingSettings: builder.query<TenantRoutingSettings, null>({
      query: () => ({
        url: '/tenant/routing-settings',
        method: 'GET'
      }),
      transformResponse: (response: { data: TenantRoutingSettings }) => response.data,
      providesTags: () => [{ type: 'TenantRoutingSettings', id: 'TENANT_ROUTING_SETTINGS' }]
    }),
    upsertTenantRoutingSettings: builder.mutation<
      TenantRoutingSettings,
      Pick<TenantRoutingSettings, 'provider' | 'config'>
    >({
      query: (body) => ({
        url: '/tenant/routing-settings',
        method: 'POST',
        body
      }),
      transformResponse: (response: { data: TenantRoutingSettings }) => response.data,
      invalidatesTags: [{ type: 'TenantRoutingSettings', id: 'TENANT_ROUTING_SETTINGS' }]
    }),
    searchTenantField: builder.query<PaginateResponse<SearchTenantField>, SearchTenantFieldParams>({
      query: (searchLoadParams) => {
        const { search, field } = searchLoadParams;

        return {
          url: '/tenant/fields/search',
          method: 'POST',
          body: { search, field }
        };
      },
      transformResponse: (response: {
        data: SearchTenantField[];
        total: number;
        more_records: boolean;
      }) => {
        return {
          content: response.data,
          total: response.total,
          more_records: response.more_records
        };
      },
      providesTags: (result) =>
        result?.content
          ? [
              ...result.content.map(({ id }) => ({ type: 'Tenant' as const, id })),
              { type: 'Tenant', id: 'TENANT_LIST' }
            ]
          : [{ type: 'Tenant', id: 'TENANT_LIST' }]
    })
  })
});

export const {
  useGetTenantAccountingSettingsQuery,
  useUpsertTenantAccountingSettingsMutation,
  useGetTenantRoutingSettingsQuery,
  useUpsertTenantRoutingSettingsMutation,
  useSearchTenantFieldQuery,
  useLazySearchTenantFieldQuery
} = tenantApi;
